import React, { useMemo, useState } from 'react'

import GradientButton from '../../components/GradientButton'
import { useT } from '../../i18n'
import type { Lot } from '../../types/lot'
import { readCapacityTables, resolveLotCapacity, withLotCapacityAssignment } from '../../utils/carryingCapacity'
import { farmDayKey, farmTimeZone } from '../../utils/farmTime'
import { lotApi } from '../../utils/lotApi'
import CapacityTablePicker, { capacityAssignmentForChoice } from './CapacityTablePicker'

// Lote → details (findings #151): the lot's Capacidad de carga table and
// "Cambiar". A change applies to the whole arrival (every active unit lot of
// the same lineage root), because the limits follow the genetics.

type FarmLike = { metadata?: Record<string, unknown> | null; timeZone?: string | null } | null | undefined

export type LotCapacityRowProps = {
  lot: Lot
  farm: FarmLike
  /** Lots of the facility (to find the arrival and its other unit lots). */
  facilityLots: ReadonlyArray<Lot>
  canEdit: boolean
  onChanged: () => void
}

/**
 * The active unit lots of the lot's arrival (same lineage root). The arrival
 * record of "Sembrar en varias unidades" has no unit and is never updated: the
 * local store refuses it (its unit lots share its name); splits find the copy
 * through the lineage (resolveLotCapacity).
 */
export const arrivalLotIds = (
  lot: Pick<Lot, 'id' | 'lineageRootId'>,
  facilityLots: ReadonlyArray<Pick<Lot, 'id' | 'lineageRootId' | 'stage' | 'productionUnitId'>>
): string[] => {
  const rootId = lot.lineageRootId ?? lot.id
  const ids = new Set<string>([lot.id])
  for (const candidate of facilityLots) {
    if ((candidate.lineageRootId ?? candidate.id) !== rootId || !candidate.productionUnitId) continue
    if (!candidate.stage || candidate.stage === 'Active') ids.add(candidate.id)
  }
  return [...ids]
}

export default function LotCapacityRow({ lot, farm, facilityLots, canEdit, onChanged }: LotCapacityRowProps) {
  const t = useT()
  const [editing, setEditing] = useState(false)
  const [choice, setChoice] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const lotsById = useMemo(() => new Map(facilityLots.map((candidate) => [candidate.id, candidate])), [facilityLots])
  const resolved = resolveLotCapacity(farm, lot, lotsById)
  if (readCapacityTables(farm).length === 0 && !resolved) return null

  const dayLabel = (iso: string): string => {
    const day = farmDayKey(iso, farmTimeZone(farm as Parameters<typeof farmTimeZone>[0])) ?? iso.slice(0, 10)
    return day.split('-').reverse().join('/')
  }

  const save = async () => {
    const assignment = capacityAssignmentForChoice(farm, choice)
    if (!assignment) return
    setSaving(true)
    setError(null)
    try {
      for (const id of arrivalLotIds(lot, facilityLots)) {
        const target = lotsById.get(id) ?? (id === lot.id ? lot : null)
        if (!target) continue
        await lotApi.update(id, { metadata: withLotCapacityAssignment(target, assignment).metadata })
      }
      setEditing(false)
      onChanged()
    } catch (saveError) {
      console.warn('[lots] capacity table change failed', saveError)
      setError(t('capacity.status.saveFailed'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div data-testid="lot-capacity-row" style={{ fontSize: 13, color: '#475569', display: 'grid', gap: 6 }}>
      <span>
        <strong>{t('capacity.picker.label')}:</strong>{' '}
        {resolved
          ? resolved.source === 'lot'
            ? t('capacity.lot.copy', { name: resolved.table.name, date: resolved.assignedAt ? dayLabel(resolved.assignedAt) : '—' })
            : t('capacity.lot.facilityDefault', { name: resolved.table.name })
          : t('capacity.picker.none')}
        {canEdit && !editing ? (
          <>
            {' '}
            <button
              type="button"
              onClick={() => { setChoice(''); setEditing(true) }}
              style={{ border: 'none', background: 'none', padding: 0, color: '#1d4ed8', fontWeight: 600, cursor: 'pointer' }}
              data-testid="lot-capacity-change"
            >
              {t('capacity.lot.change')}
            </button>
          </>
        ) : null}
      </span>
      {editing ? (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <CapacityTablePicker farm={farm} value={choice} onChange={setChoice} disabled={saving} testId="lot-capacity-picker" />
          <GradientButton size="xs" variant="success" onClick={() => void save()} disabled={saving} data-testid="lot-capacity-save">
            {t('common.save')}
          </GradientButton>
          <GradientButton size="xs" variant="secondary" onClick={() => setEditing(false)} disabled={saving}>
            {t('common.cancel')}
          </GradientButton>
        </div>
      ) : null}
      {editing ? <span style={{ fontSize: 12, color: '#6b7280' }}>{t('capacity.lot.changeHelp')}</span> : null}
      {error ? <span role="alert" style={{ color: '#b91c1c', fontWeight: 600 }}>{error}</span> : null}
    </div>
  )
}
